/**
 * GBWhatsApp 主题切换核心逻辑
 * 提供主题注册、应用和切换功能
 */
import { themes, DEFAULT_THEME } from './themes.js';

const STORAGE_KEY = 'gbwhatsapp_theme';

// 将主题颜色注入到 <style id="theme-variables"> 中
export function applyThemeVariables(themeId) {
  const theme = themes.find((t) => t.id === themeId) || themes.find((t) => t.id === DEFAULT_THEME);
  if (!theme) return;

  const c = theme.colors;

  const css = `
:root,
[data-theme="${theme.id}"] {
  /* ========== 品牌主色 ========== */
  --theme-primary: ${c.primary};
  --theme-primary-dark: ${c.primaryDark};
  --theme-primary-light: ${c.primaryLight};
  --theme-primary-pale: ${c.primaryPale};
  --theme-primary-bg: ${c.primaryBg};
  --theme-primary-border: ${c.primaryBorder};

  /* ========== 渐变 ========== */
  --theme-gradient: ${c.gradient};
  --theme-gradient-light: ${c.gradientLight};
  --theme-gradient-dark: ${c.gradientDark};
  --theme-logo-gradient: ${c.logoGradient};
  --theme-footer-gradient: ${c.footerGradient};
  --theme-hero-gradient: ${c.heroGradient};

  /* ========== 页面背景 ========== */
  --theme-page-bg: ${c.pageBg};
  --theme-section-bg: ${c.sectionBg};
  --theme-section-alt-bg: ${c.sectionAltBg};
  --theme-card-bg: ${c.cardBg};
  --theme-card-bg2: ${c.cardBg2};

  /* ========== 文字 ========== */
  --theme-text-main: ${c.textMain};
  --theme-text-body: ${c.textBody};
  --theme-text-muted: ${c.textMuted};
  --theme-text-faint: ${c.textFaint};
  --theme-text-inverse: ${c.textInverse};
  --theme-text-brand: ${c.textBrand};
  --theme-text-brand-dark: ${c.textBrandDark};

  /* ========== 导航栏 ========== */
  --theme-nav-bg: ${c.navBg};
  --theme-nav-border: ${c.navBorder};
  --theme-nav-hover: ${c.navHover};
  --theme-nav-active: ${c.navActive};
  --theme-nav-text: ${c.navText};
  --theme-mobile-menu-bg: ${c.mobileMenuBg};
  --theme-mobile-menu-border: ${c.mobileMenuBorder};

  /* ========== 按钮 ========== */
  --theme-btn-bg: ${c.btnBg};
  --theme-btn-bg-dark: ${c.btnBgDark};
  --theme-btn-text: ${c.btnText};
  --theme-btn-text-dark: ${c.btnTextDark};
  --theme-btn-shadow: ${c.btnShadow};
  --theme-btn-shadow-dark: ${c.btnShadowDark};
  --theme-btn-radius: ${c.btnRadius};
  --theme-btn-radius-sm: ${c.btnRadiusSm};
  --theme-btn-border: ${c.btnBorder};

  /* Ghost 按钮 */
  --theme-ghost-btn-bg: ${c.ghostBtnBg};
  --theme-ghost-btn-text: ${c.ghostBtnText};
  --theme-ghost-btn-border: ${c.ghostBtnBorder};

  /* 卡片下载按钮 */
  --theme-card-download-bg: ${c.cardDownloadBg};
  --theme-card-download-hover: ${c.cardDownloadHover};
  --theme-card-download-text: ${c.cardDownloadText};

  /* 底部固定按钮 */
  --theme-fixed-btn-bg: ${c.fixedBtnBg};
  --theme-fixed-btn-text: ${c.fixedBtnText};

  /* ========== 卡片 ========== */
  --theme-card-border: ${c.cardBorder};
  --theme-card-border-hover: ${c.cardBorderHover};
  --theme-card-shadow: ${c.cardShadow};
  --theme-card-shadow-md: ${c.cardShadowMd};
  --theme-card-shadow-lg: ${c.cardShadowLg};
  --theme-card-radius: ${c.cardRadius};

  /* ========== 底部栏 ========== */
  --theme-footer-bg: ${c.footerBg};
  --theme-footer-bg2: ${c.footerBg2};
  --theme-footer-text: ${c.footerText};
  --theme-footer-text-muted: ${c.footerTextMuted};
  --theme-footer-link: ${c.footerLink};
  --theme-footer-link-hover: ${c.footerLinkHover};

  /* ========== 边框 ========== */
  --theme-border: ${c.border};
  --theme-border-mid: ${c.borderMid};
  --theme-border-strong: ${c.borderStrong};
  --theme-border-inverse: ${c.borderInverse};

  /* ========== 标签/徽章 ========== */
  --theme-badge-bg: ${c.badgeBg};
  --theme-badge-text: ${c.badgeText};
  --theme-badge-border: ${c.badgeBorder};
  --theme-badge-dot: ${c.badgeDot};

  /* 功能标签 */
  --theme-tag-works-bg: ${c.tagWorksBg};
  --theme-tag-works-text: ${c.tagWorksText};
  --theme-tag-risky-bg: ${c.tagRiskyBg};
  --theme-tag-risky-text: ${c.tagRiskyText};
  --theme-tag-info-bg: ${c.tagInfoBg};
  --theme-tag-info-text: ${c.tagInfoText};
  --theme-tag-danger-bg: ${c.tagDangerBg};
  --theme-tag-danger-text: ${c.tagDangerText};
  --theme-tag-warn-bg: ${c.tagWarnBg};
  --theme-tag-warn-text: ${c.tagWarnText};

  /* ========== 表格 ========== */
  --theme-table-header-bg: ${c.tableHeaderBg};
  --theme-table-header-text: ${c.tableHeaderText};
  --theme-table-row-even: ${c.tableRowEven};
  --theme-table-row-odd: ${c.tableRowOdd};
  --theme-table-border: ${c.tableBorder};
  --theme-table-cell-highlight: ${c.tableCellHighlight};
  --theme-table-cell-warn: ${c.tableCellWarn};
  --theme-table-cell-danger: ${c.tableCellDanger};

  /* ========== 专题区块 ========== */
  --theme-highlight-section-bg: ${c.highlightSectionBg};
  --theme-highlight-section-border: ${c.highlightSectionBorder};
  --theme-callout-bg: ${c.calloutBg};
  --theme-callout-border: ${c.calloutBorder};
  --theme-callout-text: ${c.calloutText};

  /* ========== 评价 verdict ========== */
  --theme-verdict-green-bg: ${c.verdictGreenBg};
  --theme-verdict-green-text: ${c.verdictGreenText};
  --theme-verdict-red-bg: ${c.verdictRedBg};
  --theme-verdict-red-text: ${c.verdictRedText};

  /* ========== 快速导航 ========== */
  --theme-quick-nav-bg: ${c.quickNavBg};
  --theme-quick-nav-border: ${c.quickNavBorder};
  --theme-quick-nav-text: ${c.quickNavText};
  --theme-quick-nav-num: ${c.quickNavNum};
  --theme-quick-nav-hover-bg: ${c.quickNavHoverBg};
  --theme-quick-nav-hover-text: ${c.quickNavHoverText};

  /* ========== FAQ ========== */
  --theme-faq-section-bg: ${c.faqSectionBg};
  --theme-faq-section-border: ${c.faqSectionBorder};
  --theme-faq-question-bg: ${c.faqQuestionBg};
  --theme-faq-question-text: ${c.faqQuestionText};
  --theme-faq-answer-bg: ${c.faqAnswerBg};

  /* ========== 回到顶部 ========== */
  --theme-backtop-bg: ${c.backtopBg};
  --theme-backtop-shadow: ${c.backtopShadow};
  --theme-backtop-icon: ${c.backtopIcon};

  /* ========== 危险/警告 ========== */
  --theme-risk-text: ${c.riskText};
  --theme-danger-bg: ${c.dangerBg};
  --theme-danger-text: ${c.dangerText};
  --theme-warn-bg: ${c.warnBg};
  --theme-warn-text: ${c.warnText};
  --theme-info-bg: ${c.infoBg};
  --theme-info-text: ${c.infoText};

  /* ========== 特殊样式 ========== */
  --theme-divider-color: ${c.dividerColor};
  --theme-divider-light: ${c.dividerLight};
  --theme-selection-bg: ${c.selectionBg};
  --theme-scrollbar-thumb: ${c.scrollbarThumb};

  /* ========== 下载页专用 ========== */
  --theme-dlcard-bg: ${c.dlcardBg};
  --theme-dlcard-border: ${c.dlcardBorder};
  --theme-dlcard-title-text: ${c.dlcardTitleText};
  --theme-dlcard-sub-text: ${c.dlcardSubText};
  --theme-dlprice-tag-bg: ${c.dlpriceTagBg};
  --theme-dlprice-tag-text: ${c.dlpriceTagText};
  --theme-dlverified-bg: ${c.dlverifiedBg};
  --theme-dlverified-text: ${c.dlverifiedText};

  /* 下载表格 */
  --theme-guide-table-border: ${c.guideTableBorder};
  --theme-guide-table-header-bg: ${c.guideTableHeaderBg};
  --theme-guide-table-cell-bg: ${c.guideTableCellBg};
  --theme-guide-table-text: ${c.guideTableText};
}
`;

  let styleEl = document.getElementById('theme-variables');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'theme-variables';
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;
}

// 获取当前主题 ID
export function getCurrentThemeId() {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
}

// 设置主题
export function setTheme(themeId) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, themeId);
  applyThemeVariables(themeId);
  document.documentElement.setAttribute('data-theme', themeId);
}

// 初始化主题
export function initTheme() {
  if (typeof window === 'undefined') return;
  const themeId = getCurrentThemeId();
  applyThemeVariables(themeId);
  document.documentElement.setAttribute('data-theme', themeId);
}

export { themes, DEFAULT_THEME };
